import React, {useEffect, useState, useRef} from 'react';
import {useClickAway} from 'react-use';
import classNames from 'classnames';
import {curry} from 'ramda';
import {useParams} from 'react-router-dom';
import wrap from 'wraptile';
import {
    differenceInDays,
    differenceInHours,
    differenceInMinutes,
} from 'date-fns';
import {
    DeleteQcReportNoteParams,
    NoteType,
    EditQcReportNoteBody,
} from '@pluto/@types';
import CircularProgress from '@material-ui/core/CircularProgress';
import CommentIcon from '@material-ui/icons/Comment';
import {
    QCReportsNotesState,
    QCReportReasonNote,
    QCReportsReasonsState,
    QCReportsReasonsItem,
} from '../QCReportsReasonsTypes';
import {
    QCReportsNote,
    SetEditNoteIdType,
} from './QcReportsNote';
import './QCReportsNotes.scss';

type DeleteQcReportNoteType = (params: DeleteQcReportNoteParams, noteType: NoteType) => void;
type EditQcReportNoteType = (params: DeleteQcReportNoteParams, body: EditQcReportNoteBody, noteType: NoteType) => void;
export interface QCReportsContainerProps {
    noteType: NoteType,
    qcReportsReasons: QCReportsReasonsState,
    qcReportsNotes: QCReportsNotesState;
    qcReportsNotesSet: (id: string, noteType: NoteType) => void;
    resetQcReportsNotes: () => void;
    editQcReportsNote: EditQcReportNoteType;
    deleteQcReportNote: DeleteQcReportNoteType;
    jobDeliveryDate?: string;
}

export const handleFetchQcReportNotes = wrap((qcReportsNotesSet: (id: string, noteType: NoteType) => void, resetQcReportsNotes: () => void, jobId: string, noteType: NoteType) => {
    qcReportsNotesSet(jobId, noteType);
    
    return resetQcReportsNotes;
});

export const handleDeleteQcReportNote = wrap((deleteQcReportNote: DeleteQcReportNoteType, params: DeleteQcReportNoteParams, noteType: NoteType) => {
    deleteQcReportNote(params, noteType);
});

export const handleEditQcReportNote = curry((editQcReportsNote: EditQcReportNoteType, params: DeleteQcReportNoteParams, noteType: NoteType, body: EditQcReportNoteBody) => {
    editQcReportsNote(params, body, noteType);
});

export const getLastUpdateValue = (updatedAt: string, currentDate: Date) => {
    const lastUpdatedDate = new Date(updatedAt);
    
    const minutesDifference = differenceInMinutes(currentDate, lastUpdatedDate);
    const hoursDifference = differenceInHours(currentDate, lastUpdatedDate);
    const daysDifference = differenceInDays(currentDate, lastUpdatedDate);
    
    let lastUpdatedValue = `${minutesDifference} mins ago`;
    
    if (minutesDifference > 60) {
        lastUpdatedValue = `${hoursDifference} hours ago`;
        
        if (hoursDifference > 24) {
            lastUpdatedValue = `${daysDifference} days ago`;
        }
    }
    
    return lastUpdatedValue;
};

export const mapReportNote = curry((deleteQcReportNote: DeleteQcReportNoteType, editQcReportsNote: EditQcReportNoteType, setEditNoteId: SetEditNoteIdType, editNoteId: number | null, qcJobId: string, noteType: NoteType, reasonData: QCReportsReasonsItem[], jobDeliveryDate: any, item: QCReportReasonNote) => {
    const {
        createdByUser,
        id,
        value,
        timespanCategoryId,
        updatedAt,
    } = item;
    const reasonLabelObj = reasonData.find((reason) => reason.id === timespanCategoryId);
    const {name} = createdByUser;
    const deleteParams = {
        qcJobId,
        timespanId: id,
    };
    const currentDate = new Date();
    const lastUpdatedValue = getLastUpdateValue(updatedAt, currentDate);
    
    return (
        <QCReportsNote
            jobDeliveryDate={jobDeliveryDate}
            setEditNoteId={setEditNoteId}
            editNoteId={editNoteId}
            noteType={noteType}
            key={id}
            id={id}
            name={name}
            timespanCategoryId={timespanCategoryId}
            lastUpdatedValue={lastUpdatedValue}
            onEditNote={handleEditQcReportNote(editQcReportsNote, deleteParams, noteType)}
            onDeleteClick={handleDeleteQcReportNote(deleteQcReportNote, deleteParams, noteType)}
            reasonLabel={reasonLabelObj?.label}
            reasonValue={value}
        />
    );
});

export const clickedAway = wrap((setEditNoteId: SetEditNoteIdType) => {
    setEditNoteId(null);
});

export const getNoNotesNotification = (isNoNotesShown: boolean) => {
    if (isNoNotesShown) {
        return (
            <div className="qc-reports__no-notes">
                <CommentIcon
                    className="qc-reports__no-notes_icon"
                />
                <div className="qc-reports__no-notes_text">
                    No notes added yet
                </div>
            </div>
        );
    }
};

const QCReportsContainer = (props: QCReportsContainerProps) => {
    const {
        qcReportsNotes,
        editQcReportsNote,
        qcReportsReasons,
        qcReportsNotesSet,
        deleteQcReportNote,
        resetQcReportsNotes,
        noteType,
        jobDeliveryDate,
    } = props;
    const {id} = useParams<{id: string}>();
    const [editNoteId, setEditNoteId] = useState<number | null>(null);
    const notesRef = useRef(null);
    const {data: reasonData} = qcReportsReasons;
    const {
        isLoading,
        data,
    } = qcReportsNotes;
    const fetchQcReportsNotesSet = handleFetchQcReportNotes(qcReportsNotesSet, resetQcReportsNotes, id, noteType);
    
    useClickAway(notesRef, clickedAway(setEditNoteId));
    useEffect(fetchQcReportsNotesSet, []);
    
    if (isLoading) {
        return <CircularProgress />;
    }
    
    return (
        <div
            className={classNames('qc-reports__notes-wrapper', {
                noNotes: !data.length,
            })}
            ref={notesRef}
        >
            {data.map(mapReportNote(
                deleteQcReportNote,
                editQcReportsNote,
                setEditNoteId,
                editNoteId as number,
                id,
                noteType,
                reasonData,
                jobDeliveryDate,
            ))}
            {getNoNotesNotification(!data.length)}
        </div>
    );
};

export default QCReportsContainer;
